/*
 * From https://www.redblobgames.com/grids/voronoi/
 * Copyright 2018 Red Blob Games <redblobgames@gmail.com>
 * License: Apache v2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>
 *
 * Create SVG diagrams for Delaunay and Voronoi
 */
'use strict';

/* global Vue */

const util            = require('./util');
const MeshBuilder     = require('@redblobgames/dual-mesh/create');
const {makeRandFloat} = require('@redblobgames/prng');
const Poisson         = require('poisson-disk-sampling');


const MIXTURE = 1;  // 0 for centroid, 1 for circumcenter

function extrapolate_from_center(p, center) {
    let dx = p[0] - center[0], dy = p[1] - center[1];
    return [center[0] + dx*5, center[1] + dy*5];
}

    
/** Sets the graph used for all the other layers */
Vue.component('with-graph', {
    props: {
        graph: Object,
        voronoiMix: {
            type: Number,
            default: 1,
        },
    },
    provide: function() {
        let self = this;
        let original = this.graph;
        let derived = Object.create(original);
        let t_voronoi = util.calculateAlternateCenterPositions(derived, util.circumcenter);
        derived.t_x = function(t) { return util.mix(original.t_x(t), t_voronoi[t][0], self.voronoiMix); };
        derived.t_y = function(t) { return util.mix(original.t_y(t), t_voronoi[t][1], self.voronoiMix); };
        return {graph: derived};
    },
    template: `<g><slot/></g>`,
});


/** Label placed near a reference point. */
Vue.component('a-label', {
    props: ['at', 'dx', 'dy'],
    template: '<text :transform="`translate(${at})`" :dx="dx || 0" :dy="dy || 0"><slot/></text>'
});

Vue.component('a-triangle-circulation', {
    inject: ['graph'],
    props: ['shrink'],
    data() {
        return {
            animation: null,
            rotationDegrees: 0,
        };
    },
    template: `
    <g>
      <g v-for="(_,t) in graph.numSolidTriangles" :key="t"
           :transform="center(t)">
        <path class="b-side half rotating"
           :d="circulation(t)"/>
      </g>
    </g>
`,
    mounted() {
        this.$el.ownerSVGElement.addEventListener('mouseenter', this.startAnimation);
        this.$el.ownerSVGElement.addEventListener('mouseleave', this.stopAnimation);
    },
    beforeDestroy() {
        this.stopAnimation();
        this.$el.ownerSVGElement.removeEventListener('mouseenter', this.startAnimation);
        this.$el.ownerSVGElement.removeEventListener('mouseleave', this.stopAnimation);
    },
    methods: {
        startAnimation() {
            this.animation = setInterval(() => { this.rotationDegrees -= 3; }, 60);
        },
        stopAnimation() {
            clearInterval(this.animation);
            this.animation = null;
        },
        center(t) {
            // TODO: remove redundant code with circulation(t)
            let r_out = this.graph.t_circulate_r([], t);
            let positions = r_out.map(r => this.graph.r_pos([], r));
            let incircle = util.incenter([], positions[0], positions[1], positions[2]);
            return `translate(${incircle[0]}, ${incircle[1]}) rotate(${this.rotationDegrees})`;
        },
        circulation(t) {
            const startAngle = 2.2*Math.PI;
            const endAngle = 0.3*Math.PI;
            let r_out = this.graph.t_circulate_r([], t);
            let positions = r_out.map(r => this.graph.r_pos([], r));
            let incircle = util.incenter([], positions[0], positions[1], positions[2]);
            let r = incircle[2] * (1.0 - this.shrink);
            let start = util.polarToCartesian(r, startAngle),
                end = util.polarToCartesian(r, endAngle);
            return `M ${start} A ${r} ${r} 0, 1, 0, ${end}`;
        }
    }
});

Vue.component('a-side-black-edges', {
    inject: ['graph'],
    props: {
        shrink: {
            type: Number,
            default: 0.0,
        },
        pulse: {
            type: Array,
            default() { return []; },
        },
    },
    template: `
    <g>
      <path v-for="(_,s) in graph.numSides" :key="s"
         class="b-side" :class="{ghost: graph.s_ghost(s), half: shrink &gt; 0, pulsing: false}"
         :d="b_side(s)"/>
    </g>
`,
    data() { return {animation: 0}; },
    created() {
        this._interval = setInterval(() => {this.animation++;}, 2000);
    },
    beforeDestroy() {
        clearInterval(this._interval);
    },
    methods: {
        b_side(s) {
            const shrink = this.shrink || 0.0;
            let begin = this.graph.r_pos([], this.graph.s_begin_r(s));
            let end = this.graph.r_pos([], this.graph.s_end_r(s));
            if (this.graph.r_ghost(this.graph.s_begin_r(s))) {
                begin = extrapolate_from_center(end, [300, 150]);
            } else if (this.graph.r_ghost(this.graph.s_end_r(s))) {
                end = extrapolate_from_center(begin, [300, 150]);
            }
            // NOTE: shrink feature prefers centroid t_pos, not voronoi
            let center = this.graph.t_pos([], this.graph.s_inner_t(s));
            begin = util.mixvec2(begin, center, shrink);
            end = util.mixvec2(end, center, shrink);
            return `M ${begin} L ${end}`;
        },
    }
});

Vue.component('a-side-white-edges', {
    inject: ['graph'],
    props: ['shrink'],
    template: `
    <g>
      <path v-for="(_,s) in graph.numSides" :key="s"
         class="w-side" :class="{ghost: graph.t_ghost(graph.s_outer_t(s)) || graph.s_ghost(s), half: shrink &gt; 0}"
        :d="w_side(s)"/>
    </g>
`,
    methods: {
        w_side: function(s) {
            const shrink = this.shrink || 0.0;
            let begin = this.graph.t_pos([], this.graph.s_inner_t(s));
            let end = this.graph.t_pos([], this.graph.s_outer_t(s));
            let center = this.graph.r_pos([], this.graph.s_begin_r(s));
            begin = util.mixvec2(begin, center, shrink);
            end = util.mixvec2(end, center, shrink);
            return `M ${begin} L ${end}`;
        },
    }
});

Vue.component('a-side-labels', {
    inject: ['graph'],
    template: `
    <g>
      <a-label v-for="(_,s) in graph.numSolidSides" :key="s"
        class="s" 
        dy="7"
        :at="mixvec2(mixvec2(graph.r_pos([], graph.s_begin_r(s)),
                             graph.r_pos([], graph.s_end_r(s)),
                             0.4),
                     graph.t_pos([], graph.s_inner_t(s)),
                     0.2)">
      s{{s}}
      </a-label>
    </g>
`,
    methods: {mixvec2: util.mixvec2},
});

Vue.component('a-region-polygons', {
    inject: ['graph'],
    props: ['opacity-fn'],
    template: `
    <g>
      <polygon v-for="(_,r) in graph.numSolidRegions" :key="r"
               v-if="opacity(r) &gt; 0.0"
               class="r" :points="points(r)"
               :opacity="opacity(r)"/>
    </g>
`,
    methods: {
        opacity(r) {
            let value = this.opacityFn(this.graph.r_x(r), this.graph.r_y(r));
            if (value < 0.0) { value = 0.0; }
            if (value > 1.0) { value = 1.0; }
            return value;
        },
        points(r) {
            return this.graph.r_circulate_t([], r)
                .map(t => `${this.graph.t_x(t)},${this.graph.t_y(t)}`)
                .join(" ");
        },
    },
});
               
Vue.component('a-region-points', {
    inject: ['graph'],
    props: ['hover', 'radius'],
    template: `
    <g>
      <circle v-for="(_,r) in graph.numSolidRegions" :key="r"
        class="r"
        :r="radius || 10"
        @mouseover="hover('r'+r)" 
        @touchstart.passive="hover('r'+r)"
        :transform="\`translate($\{graph.r_pos([], r)})\`"/>
    </g>
`,
});

Vue.component('a-region-labels', {
    inject: ['graph'],
    template: `
    <g>
      <a-label v-for="(_,r) in graph.numSolidRegions" :key="r"
        class="r" 
        :dy="graph.r_y(r) > 150? 25 : -15" :at="graph.r_pos([], r)">
        r{{r}}
      </a-label>
    </g>
`,
});

Vue.component('a-triangle-polygons', {
    inject: ['graph'],
    props: ['opacity-fn'],
    template: `
    <g>
      <polygon v-for="(_,t) in graph.numSolidTriangles" :key="t"
               v-if="opacity(t) &gt; 0.0"
               class="t" :points="points(t)"
               :opacity="opacity(t)" />
    </g>
`,
    methods: {
        opacity(t) {
            let value = this.opacityFn(this.graph.t_x(t), this.graph.t_y(t));
            if (value < 0.0) { value = 0.0; }
            if (value > 1.0) { value = 1.0; }
            return value;
        },
        points(t) {
            return this.graph.t_circulate_r([], t)
                .map(r => `${this.graph.r_x(r)},${this.graph.r_y(r)}`)
                .join(" ");
        },
    },
});

Vue.component('a-triangle-points', {
    inject: ['graph'],
    props: ['hover', 'radius'],
    template: `
      <g>
        <circle v-for="(_,t) in graph.numTriangles" :key="t"
          :class="'t' + (graph.t_ghost(t)? ' ghost':'')" 
          :r="radius || 7"
          @mouseover="hover('t'+t)" 
          @touchstart.passive="hover('t'+t)"
          :transform="\`translate($\{graph.t_pos([], t)})\`"/>
      </g>
`,
});

Vue.component('a-triangle-labels', {
    inject: ['graph'],
    template: `
      <g>
        <a-label v-for="(_,t) in graph.numSolidTriangles" :key="t"
          class="t" 
          dy="25" 
          :at="graph.t_pos([], t)">
          t{{t}}
        </a-label>
      </g>
`,
});


const seeds1 = [
    [250, 30], [100, 260], [400, 260], [550, 30]
];

const seeds2 = [
    [320, 170], [220, 270], [400, 270],
    [530, 50], [100, 80], [300, 30],
    [50, 220], [550, 240],
];

const seeds3 = [
    [550, 310], [250, 420], [500, 500],
    [350, 620], [580, 580], [730, 400]
];

const seeds4 = [
    [300, 350], [500, 330], [700, 350],
    [390, 450], [610, 450],
    [500, 560],
];

function filterMesh(builder, rect) {
    let points = builder.getNonBoundaryPoints().filter(
        p => rect.left <= p[0] && p[0] <= rect.right && rect.top <= p[1] && p[1] <= rect.bottom
    );
    builder.clearNonBoundaryPoints()
        .addPoints(points);
    return builder;
}

function makeVariableDensityMesh() {
    let builder = new MeshBuilder({boundarySpacing: 75})
        .addPoisson(Poisson, 20);
    let highDensity = builder.getNonBoundaryPoints().filter(p => 800 <= p[0]+p[1] && p[0]+p[1] <= 1200);
    builder.clearNonBoundaryPoints()
        .addPoints(highDensity)
        .addPoisson(Poisson, 50);
    return builder.create();
}
    
const graphs = Object.freeze({
    poisson: filterMesh(new MeshBuilder({boundarySpacing: 75})
                        .addPoisson(Poisson, 50),
                        {left: 0, right: 1000, top: 100, bottom: 800})
        .create(),
    poissonsmall: new MeshBuilder({boundarySpacing: 200})
        .addPoisson(Poisson, 150)
        .create(),
    triangle: new MeshBuilder()
        .addPoints(seeds1.slice(0, 3))
        .create(),
    small: new MeshBuilder()
        .addPoints(seeds1)
        .create(),
    medium: new MeshBuilder()
        .addPoints(seeds2)
        .create(),
    medium2: new MeshBuilder()
        .addPoints(seeds3)
        .create(),
    fourtriangles: new MeshBuilder()
        .addPoints(seeds4)
        .create(),
    blended0: new MeshBuilder()
        .addPoints(util.makeSquareGrid(50).filter(p => p[0] < 350))
        .addPoints(util.makeHexGrid(50).filter(p => p[0] > 650))
        .addPoisson(Poisson, 40)
        .create(),
    blended1: new MeshBuilder()
        .addPoints(util.makeSquareGrid(50).filter(p => (p[1]/100 | 0) % 3 === 0))
        .addPoints(util.makeHexGrid(50).filter(p => (p[1]/100 | 0) % 3 !== 0))
        .create(),
    blended2: new MeshBuilder({boundarySpacing: 75})
        .addPoints(util.makeRadialGrid(50).filter(p => (p[0]/200 | 0) % 3 !== 0))
        .addPoints(util.makeHexGrid(50).filter(p => (p[0]/200 | 0) % 3 === 0))
        .create(),
    blended3: new MeshBuilder()
        .addPoints(util.makeSquareGrid(50).filter(p => ((p[0]+2*p[1])/400 | 0) % 3 === 0))
        .addPoisson(Poisson, 50)
        .create(),
    radial: new MeshBuilder({boundarySpacing: 75})
        .addPoints(util.makeRadialGrid(50))
        .create(),
    density: makeVariableDensityMesh(),
});

    
function makeDiagram(element) {
    new Vue({
        el: element,
        data: {
            highlight: '',
            radius: 90,
            voronoiMix: 1,
            graphs: graphs,
        },
        computed: {
            highlightId: function() {
                return parseInt(this.highlight.slice(1));
            },
        },
        methods: {
            hover: function(label) {
                this.highlight = label;
            },
            format_array: function(label, array) {
                return array.map((x) => (x === null || x < 0)? '(null)' : label+x).join(" ");
            },
        }
    });
}

for (let diagram of document.querySelectorAll("figure")) {
    makeDiagram(diagram);
}

for (let icon of document.querySelectorAll("icon")) {
    let type = icon.getAttribute('type');
    let shape = type === 'black' || type === 'white'
        ? [-5, -5, 30, 10]
        : [-5, -5, 10, 10];
    let label, contents;
    switch(type) {
    case 'red': label = 'triangle vertex'; contents = '<circle r="3.5" class="r"/>'; break;
    case 'blue': label = 'region vertex'; contents = '<circle r="3" class="t"/>'; break;
    case 'white': label = 'region edge'; contents = '<line x2="20" class="w-side"/>'; break;
    case 'black': label = 'triangle edge'; contents = '<line x2="20" class="b-side"/>'; break;
    case 'region': label = 'region'; contents = '<polygon class="r" points="0,-5 -5,-1 -3,4 3,4 5,-1"/>'; break;
    case 'triangle': label = 'triangle'; contents = '<polygon class="t" points="0,-5 -4,4 4,4"/>'; break;
    }

    icon.outerHTML = `<svg class="icon ${type}" alt="${label} icon" width="${shape[2]/10}em" viewBox="${shape.join(' ')}">${contents}</svg>`;
}
